import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { getThread, getThreadPosts, replyThread, toggleReplyLike, getReplyLikes } from '../../services/forumApi';
import { authenticatedRequest } from '../../services/authApi';
import EmojiPicker from 'emoji-picker-react';
import { REST_API_ORIGIN } from '../../platform/endpoints';
import { localizedValue } from '../../localization/presentationLanguage';
import { getAuthToken } from '../../utils/auth';
const forumText = (es, en) => localizedValue({ es, en });

const AUTHOR_SEED_ID = '000000000000000000000000';

function isSeedId(v) {
  try {
    if (!v) return true; // null/undefined => considerar semilla oficial
    if (typeof v === 'string') return v === AUTHOR_SEED_ID;
    if (typeof v === 'object') {
      return (
        v._id === AUTHOR_SEED_ID ||
        v.id === AUTHOR_SEED_ID ||
        v.$oid === AUTHOR_SEED_ID ||
        (typeof v._id === 'object' && (v._id.$oid === AUTHOR_SEED_ID || v._id.id === AUTHOR_SEED_ID))
      );
    }
    return false;
  } catch { return false; }
}

// Emoji por signo (case-insensitive)
const SIGN_EMOJI = {
  capricornio: '♑',
  acuario: '♒',
  piscis: '♓',
  aries: '♈',
  tauro: '♉',
  geminis: '♊',
  cancer: '♋',
  leo: '♌',
  virgo: '♍',
  libra: '♎',
  escorpio: '♏',
  sagitario: '♐',
};
function signToEmoji(sign) {
  if (!sign) return null;
  const key = String(sign).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return SIGN_EMOJI[key] || null;
}
function signToColor(sign) {
  const k = (sign || '').toLowerCase().normalize('NFD').replace(/[^a-z]/g, '');
  switch (k) {
    case 'aries': return '#FF6B6B';
    case 'tauro': return '#7CB342';
    case 'geminis': return '#29B6F6';
    case 'cancer': return '#26A69A';
    case 'leo': return '#FFA726';
    case 'virgo': return '#8D6E63';
    case 'libra': return '#AB47BC';
    case 'escorpio': return '#EF5350';
    case 'sagitario': return '#FFB300';
    case 'capricornio': return '#6D4C41';
    case 'acuario': return '#42A5F5';
    case 'piscis': return '#26C6DA';
    default: return '#DAA520';
  }
}

const API_BASE = REST_API_ORIGIN;

function normalizePhotoUrl(pathOrUrl) {
  try {
    if (!pathOrUrl) return null;
    const s = String(pathOrUrl).trim();
    if (!s) return null;
    if (/^data:image\//i.test(s)) return s;
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith('/avatars/')) return `${API_BASE}${s}`;
    if (s.startsWith('/profile_photos/')) return `${API_BASE}${s}`;
    if (s.startsWith('/')) return `${API_BASE}${s}`;
    return `${API_BASE}/${s.replace(/^\/+/, '')}`;
  } catch {
    return null;
  }
}

function cleanForumBody(text) {
  try {
    return String(text || '')
      .split('\n')
      .filter((line) => {
        const s = String(line || '').trim();
        if (!s) return true;
        return !(
          s.startsWith('#seedthread') ||
          s.startsWith('#seedreply') ||
          s.startsWith('#liveforumreply:') ||
          s.startsWith('#liveforumthread:')
        );
      })
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  } catch {
    return String(text || '').trim();
  }
}

const __forumAuthorPhotoCache = new Map();
const __forumAuthorPhotoInflight = new Map();
const __forumAuthorPhotoMissUntil = new Map();
let __forumAuthorPhotoQueue = Promise.resolve();

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function lookupForumAuthorPhotoById(userId) {
  try {
    const id = String(userId || '').trim();
    if (!id) return null;

    const cached = __forumAuthorPhotoCache.get(id);
    if (typeof cached === 'string' && cached) return cached;

    const blockedUntil = __forumAuthorPhotoMissUntil.get(id) || 0;
    if (blockedUntil > Date.now()) return null;

    const sessionKey = `forum:author-photo:${id}`;
    try {
      const sessionHit = sessionStorage.getItem(sessionKey);
      if (sessionHit) {
        __forumAuthorPhotoCache.set(id, sessionHit);
        return sessionHit;
      }
    } catch {}

    if (__forumAuthorPhotoInflight.has(id)) {
      return await __forumAuthorPhotoInflight.get(id);
    }

    const job = (__forumAuthorPhotoQueue = __forumAuthorPhotoQueue.then(async () => {
      await wait(450);

      try {
        const res = await fetch(`${API_BASE}/api/users/by-id/${encodeURIComponent(id)}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
        });

        if (res.status === 429) {
          __forumAuthorPhotoMissUntil.set(id, Date.now() + (10 * 60 * 1000));
          return null;
        }

        const data = await res.json().catch(() => null);
        if (!res.ok || !data) {
          __forumAuthorPhotoMissUntil.set(id, Date.now() + (5 * 60 * 1000));
          return null;
        }

        const normalized = normalizePhotoUrl(data.avatarUrl || data.photoPath || null);
        if (normalized) {
          __forumAuthorPhotoCache.set(id, normalized);
          try { sessionStorage.setItem(sessionKey, normalized); } catch {}
          return normalized;
        }

        __forumAuthorPhotoMissUntil.set(id, Date.now() + (5 * 60 * 1000));
        return null;
      } catch {
        __forumAuthorPhotoMissUntil.set(id, Date.now() + (2 * 60 * 1000));
        return null;
      } finally {
        __forumAuthorPhotoInflight.delete(id);
      }
    }));

    __forumAuthorPhotoInflight.set(id, job);
    return await job;
  } catch {
    return null;
  }
}




// Helper to mark a forum notification as read, best effort, compatible with different backend payloads
async function bestEffortMarkForumNotifRead(notifId) {
  try {
    const id = notifId ? String(notifId) : '';
    if (!id) return;

    const token = getAuthToken();
    if (!token) return;

    const url = `${API_BASE}/api/forum/notifications/mark-read`;

    // Be compatible with multiple backend payload shapes
    const body = {
      ids: [id],
      notificationIds: [id],
      id,
    };

    await authenticatedRequest(url, {
      rawResponse: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
      diagSource: 'ForumThreadView.markNotificationRead',
    }).catch(() => {});
  } catch {
    // no-op
  }
}

//function relativeTime(iso) {
  //try {
    //const d = new Date(iso);
    //const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    //if (diff < 60) return 'justo ahora';
   // const mins = Math.floor(diff / 60);
    //if (mins < 60) return `hace ${mins} min`;
    //const hrs = Math.floor(mins / 60);
    //if (hrs < 24) return `hace ${hrs} h`;
    //const days = Math.floor(hrs / 24);
   // return `hace ${days} d`;
  //} catch { return ''; }
//}

function threadStatusLabel(thread) {
  const replies =
    thread?.counters?.replies ??
    thread?.repliesCount ??
    0;

  return replies > 0
    ? '🪐 Debate abierto'
    : forumText('Pregunta activa', 'Active question');
}


function postAuthorName(p) {
  try {
    if (isSeedId(p?.authorId)) return 'Zodiacus Oficial 🪐';
    return (
      p.authorFullName ||
      p.authorName ||
      (p.author && (p.author.fullName || p.author.name)) ||
      (typeof p.author === 'string' ? p.author : '') ||
      'Usuario'
    );
  } catch {
    return 'Usuario';
  }
}

function getInitials(name = '') {
  try {
    const parts = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return 'ZU';
    return parts
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join('');
  } catch {
    return 'ZU';
  }
}


function postAuthorPhoto(p) {
  try {
    const direct = p.authorPhotoUrl || p.authorPhotoPath || p.photoUrl || p.photoPath || p.avatarUrl;
    const nested = p.author && (p.author.photoUrl || p.author.photoPath || p.author.avatarUrl);
    const resolved = normalizePhotoUrl(direct || nested || null);
    return resolved || null;
  } catch {
    return null;
  }
}

function getAuthorId(p) {
  try {
    const cand = p?.authorId || (p?.author && (p.author._id || p.author.id)) || null;
    return toIdString(cand);
  } catch { return null; }
}

// ==== Helpers to robustly read IDs (handle _id, id, postId, {$oid}, objects, etc.) ====
function toIdString(v) {
  try {
    if (!v) return null;
    if (typeof v === 'string') return v;
    // Mongo exported shapes
    if (typeof v === 'object') {
      if (v.$oid) return String(v.$oid);
      if (v._id) return toIdString(v._id);
      if (v.id) return toIdString(v.id);
    }
    // Fallback toString
    return String(v);
  } catch { return null; }
}


function collectAllIds(p) {
  const out = new Set();
  [p && p._id, p && p.id, p && p.postId]
    .map(toIdString)
    .filter(Boolean)
    .forEach((s) => out.add(String(s)));
  // also nested shapes
  if (p && typeof p._id === 'object' && p._id.$oid) out.add(String(p._id.$oid));
  return Array.from(out);
}

const __forumPostIdsCache = new WeakMap();

function collectAllIdsCached(p) {
  try {
    if (!p || typeof p !== 'object') return collectAllIds(p);
    const hit = __forumPostIdsCache.get(p);
    if (hit) return hit;
    const ids = collectAllIds(p);
    __forumPostIdsCache.set(p, ids);
    return ids;
  } catch {
    return collectAllIds(p);
  }
}

function getParentIdFromAny(p) {
  const candidates = [
    p.parentId,
    p.parent,
    p.replyToId,
    p.replyTo,
    p.inReplyToId,
    p.inReplyTo,
    p.parentPostId,
    p.parentPost,
    (p.parent && (p.parent._id || p.parent.id)),
    (p.replyTo && (p.replyTo._id || p.replyTo.id)),
    (p.inReplyTo && (p.inReplyTo._id || p.inReplyTo.id)),
    (p.parentPost && (p.parentPost._id || p.parentPost.id))
  ].filter(Boolean);
  if (!candidates.length) return null;
  return toIdString(candidates[0]);
}

// Normalize post shape and IDs

function normalizePostShape(raw) {
  const p = raw || {};
  const id =
    String(p._id || p.id || (p.postId ?? '')) || '';
  // Parent can come in many shapes across API versions/DB layers
  const parentIdCandidates = [
    p.parentId,
    // Common aliases
    p.parent,
    p.replyToId,
    p.replyTo,
    p.inReplyToId,
    p.inReplyTo,
    p.parentPostId,
    p.parentPost,
    // Nested objects
    (p.parent && (p.parent._id || p.parent.id)),
    (p.replyTo && (p.replyTo._id || p.replyTo.id)),
    (p.inReplyTo && (p.inReplyTo._id || p.inReplyTo.id)),
    (p.parentPost && (p.parentPost._id || p.parentPost.id))
  ].filter(Boolean);

  const parentId = parentIdCandidates.length ? String(parentIdCandidates[0]) : null;

  return {
    ...p,
    _id: id || undefined,
    id: id || undefined,
    parentId: parentId ? String(parentId) : undefined,
  };
}

// Cuenta descendientes en todo el subárbol (hijos, nietos, etc.)
function countDescendants(node) {
  if (!node || !Array.isArray(node.children)) return 0;
  let total = 0;
  for (const c of node.children) total += 1 + countDescendants(c);
  return total;
}

const ForumPostNode = React.memo(function ForumPostNode({
  node,
  idx,
  depth = 0,
  suppressChildren = false,
  showToggles = true,
  isMobile,
  likesByReply,
  likeCountsByReply,
  likeBusyMap,
  currentUserId,
  contactsAvailable,
  thread,
  expandedNodes,
  toggleExpanded,
  resolveParentInfo,
  onToggleLike,
  setToast,
  likeToastTimerRef,
  setReplyTo,
  focusReplyBox,
  addToContacts,
}) {
  const p = node.post;
  const borderColor = signToColor(p.authorZodiacSign);
  const isAlt = idx % 2 === 1;
  const info = (p.parentId || p.parent) ? (resolveParentInfo(p) || {}) : null;
  const authorIdResolved = getAuthorId(p);
   const initialResolvedPhoto = postAuthorPhoto(p);
  const [fallbackResolvedPhoto, setFallbackResolvedPhoto] = useState(null);
  const resolvedPhoto = initialResolvedPhoto || fallbackResolvedPhoto;

  const isExpandedLocal = (id) => !!expandedNodes[String(id)];

    useEffect(() => {
    let cancelled = false;

    if (initialResolvedPhoto) {
      setFallbackResolvedPhoto(null);
      return undefined;
    }

    if (!authorIdResolved || isSeedId(authorIdResolved)) return undefined;

    (async () => {
      const found = await lookupForumAuthorPhotoById(authorIdResolved);
      if (!cancelled && found) {
        setFallbackResolvedPhoto(found);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authorIdResolved, initialResolvedPhoto]);

  return (
    <li
      id={`post-${p._id || p.id}`}
      key={p._id || p.id}
      style={{
        ...styles.post,
        ...(isAlt ? styles.postAlt : null),
        borderLeft: `3px solid ${borderColor}`,
        animation: 'fadeUp .28s ease both',
        animationDelay: `${idx * 40}ms`,
        marginLeft: depth ? 8 : 0
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap:6, rowGap:4, marginBottom:4, flexWrap:'wrap', width:'100%', minWidth:0 }}>
        {resolvedPhoto && authorIdResolved ? (
          <Link to={`/profile/${authorIdResolved}`} style={{ display:'inline-flex' }}>
            <img
              src={resolvedPhoto}
              alt=""
              style={{ ...styles.avatar, ...(isMobile ? styles.avatarMobile : null) }}
              referrerPolicy="no-referrer"
              loading="lazy"
              decoding="async"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </Link>
        ) : resolvedPhoto ? (
          <img
            src={resolvedPhoto}
            alt=""
            style={{ ...styles.avatar, ...(isMobile ? styles.avatarMobile : null) }}
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : authorIdResolved ? (
          <Link to={`/profile/${authorIdResolved}`} style={{ display:'inline-flex', textDecoration:'none' }}>
            <div style={{ ...styles.avatarFallback, ...(isMobile ? styles.avatarFallbackMobile : null) }}>
              {getInitials(postAuthorName(p))}
            </div>
          </Link>
        ) : (
          <div style={{ ...styles.avatarFallback, ...(isMobile ? styles.avatarFallbackMobile : null) }}>
            {getInitials(postAuthorName(p))}
          </div>
        )}

        {authorIdResolved ? (
          <Link to={`/profile/${authorIdResolved}`} style={styles.authorLink}>
            {postAuthorName(p)}
          </Link>
        ) : (
          <span style={styles.authorName}>{postAuthorName(p)}</span>
        )}

        {signToEmoji(p.authorZodiacSign) && (
          <span style={styles.zodiac} title={p.authorZodiacSign}>{signToEmoji(p.authorZodiacSign)}</span>
        )}

        {!isSeedId(getAuthorId(p)) && authorIdResolved && contactsAvailable && String(authorIdResolved) !== String(currentUserId) && (
          <button
            type="button"
            onClick={() => addToContacts(authorIdResolved)}
            title={forumText('Ver perfil', 'View profile')}
            style={{
              ...styles.contactBtn,
              ...(isMobile ? styles.contactBtnMobile : null),
            }}
          >
            {forumText('Ver perfil', 'View profile')}
          </button>
        )}

        {toIdString(getAuthorId(p)) === toIdString(thread?.authorId) && (
          <span style={{ ...styles.opBadge, ...(isMobile ? styles.opBadgeMobile : null) }} title="Autor del tema">
            <span aria-hidden="true" style={{ marginRight: 4, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.25))' }}>👑</span>
            <span>Creador</span>
          </span>
        )}

        {!isMobile && <span aria-hidden="true" style={styles.bullet}>•</span>}

        <span style={{ ...styles.meta, ...(isMobile ? styles.metaTimeMobile : null) }}>
          💬 Respuesta
        </span>

        {info ? (
          <span
            style={{
              ...styles.inReplyTo,
              ...(isMobile ? styles.inReplyToMobile : null),
              maxWidth: '100%',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
            title={info.author ? ('En respuesta a ' + info.author) : 'En respuesta a'}
          >
            En respuesta a {info.author || '—'}
          </span>
        ) : null}

        <div style={styles.grow} />
      </div>

      <div style={styles.postBody}>{cleanForumBody(p.body)}</div>

      {(() => {
        const replyKey = String(p._id || p.id || '');
        const liked = !!likesByReply[replyKey];
        return (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginTop:6, flexWrap:'wrap' }}>
            <button
              type="button"
              onClick={() => {
                const nextWillBeLiked = !liked;
                onToggleLike(replyKey);
                try {
                  setToast(nextWillBeLiked ? 'Guardando tu Me gusta…' : 'Quitando tu Me gusta…');
                  if (likeToastTimerRef.current) clearTimeout(likeToastTimerRef.current);
                  likeToastTimerRef.current = setTimeout(() => {
                    setToast('');
                    likeToastTimerRef.current = null;
                  }, 900);
                } catch {}
              }}
              aria-pressed={liked}
              aria-disabled={!!likeBusyMap[replyKey]}
              title={liked ? 'Quitar me gusta' : 'Me gusta'}
              style={{
                border:'1px solid rgba(218,165,32,0.50)',
                background: liked
                  ? 'linear-gradient(135deg, #FFE28A, #D9A521)'
                  : 'rgba(218,165,32,0.08)',
                color: liked ? '#240033' : '#FFD978',
                borderRadius: 999,
                padding:'6px 10px',
                fontWeight: 900,
                fontSize: 12,
                cursor: !!likeBusyMap[replyKey] ? 'wait' : 'pointer',
                opacity: !!likeBusyMap[replyKey] ? 0.75 : 1,
                boxShadow:'inset 0 1px 0 rgba(255,255,255,0.05)'
              }}
            >
              {liked ? '❤️ Me gusta' : '🤍 Me gusta'} · {likeCountsByReply[replyKey] ?? 0}
            </button>

            <button
              type="button"
              onClick={() => {
                setReplyTo({
                  id: p._id || p.id,
                  author: postAuthorName(p),
                 excerpt: cleanForumBody(p.body).slice(0, 140)
                });
                focusReplyBox();
              }}
              style={styles.replyInlineBtn}
              title="Responder a este comentario"
              aria-label="Responder a este comentario"
            >
              Responder
            </button>
          </div>
        );
      })()}

      {(!suppressChildren && node.children && node.children.length > 0) && (() => {
        const nodeId = String(p._id || p.id || '');
        const totalDesc = countDescendants(node);
        const expanded = isExpandedLocal(nodeId);

        const visibleChildren = expanded
          ? node.children
          : (node.children.length ? [node.children[0]] : []);

        const visibleCountCollapsed = expanded ? totalDesc : (visibleChildren.length > 0 ? 1 : 0);
        const hiddenCount = Math.max(0, totalDesc - visibleCountCollapsed);

        return (
          <div>
            <ul style={{
              listStyle:'none',
              paddingLeft: 14,
              margin: '8px 0 0',
              borderLeft: '1px solid rgba(218,165,32,0.22)'
            }}>
              {visibleChildren.map((child, cidx) => (
                <ForumPostNode
                  key={child.post?._id || child.post?.id || `${nodeId}_${cidx}`}
                  node={child}
                  idx={cidx}
                  depth={depth + 1}
                  suppressChildren={!expanded}
                  showToggles={false}
                  isMobile={isMobile}
                  likesByReply={likesByReply}
                  likeCountsByReply={likeCountsByReply}
                  likeBusyMap={likeBusyMap}
                  currentUserId={currentUserId}
                  contactsAvailable={contactsAvailable}
                  thread={thread}
                  expandedNodes={expandedNodes}
                  toggleExpanded={toggleExpanded}
                  resolveParentInfo={resolveParentInfo}
                  onToggleLike={onToggleLike}
                  setToast={setToast}
                  likeToastTimerRef={likeToastTimerRef}
                  setReplyTo={setReplyTo}
                  focusReplyBox={focusReplyBox}
                  addToContacts={addToContacts}
                  
                />
              ))}
            </ul>

            {showToggles && hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => toggleExpanded(nodeId)}
                style={styles.toggleRepliesBtn}
                aria-expanded={expanded}
                aria-label={forumText(`Ver más respuestas (${hiddenCount})`, `View more replies (${hiddenCount})`)}
                title={forumText(`Ver más respuestas (${hiddenCount})`, `View more replies (${hiddenCount})`)}
              >
                {forumText(`Ver más respuestas (${hiddenCount})`, `View more replies (${hiddenCount})`)}
              </button>
            )}

            {showToggles && expanded && totalDesc > 1 && (
              <button
                type="button"
                onClick={() => toggleExpanded(nodeId)}
                style={{ ...styles.toggleRepliesBtn, ...styles.toggleRepliesBtnAlt }}
                aria-expanded={expanded}
                aria-label="Ocultar respuestas"
                title="Ocultar respuestas"
              >
                Ocultar respuestas
              </button>
            )}
          </div>
        );
      })()}
    </li>
  );
}, (prev, next) => {
  const prevId = String(prev?.node?.post?._id || prev?.node?.post?.id || '');
  const nextId = String(next?.node?.post?._id || next?.node?.post?.id || '');
  if (prevId !== nextId) return false;

  const prevLiked = !!prev.likesByReply?.[prevId];
  const nextLiked = !!next.likesByReply?.[nextId];
  if (prevLiked !== nextLiked) return false;

  const prevCount = prev.likeCountsByReply?.[prevId] ?? 0;
  const nextCount = next.likeCountsByReply?.[nextId] ?? 0;
  if (prevCount !== nextCount) return false;

  const prevBusy = !!prev.likeBusyMap?.[prevId];
  const nextBusy = !!next.likeBusyMap?.[nextId];
  if (prevBusy !== nextBusy) return false;

  const prevExpanded = !!prev.expandedNodes?.[prevId];
  const nextExpanded = !!next.expandedNodes?.[nextId];
  if (prevExpanded !== nextExpanded) return false;

  if (prev.idx !== next.idx) return false;
  if (prev.depth !== next.depth) return false;
  if (prev.suppressChildren !== next.suppressChildren) return false;
  if (prev.showToggles !== next.showToggles) return false;
  if (prev.isMobile !== next.isMobile) return false;
  if (String(prev.currentUserId || '') !== String(next.currentUserId || '')) return false;
  if (!!prev.contactsAvailable !== !!next.contactsAvailable) return false;
  if (String(prev.thread?.authorId || '') !== String(next.thread?.authorId || '')) return false;

  const prevPost = prev?.node?.post || {};
  const nextPost = next?.node?.post || {};

  if (String(prevPost.authorPhotoUrl || prevPost.authorPhotoPath || prevPost.photoUrl || prevPost.photoPath || prevPost.avatarUrl || '') !== String(nextPost.authorPhotoUrl || nextPost.authorPhotoPath || nextPost.photoUrl || nextPost.photoPath || nextPost.avatarUrl || '')) return false;
  if (String(prevPost.authorFullName || prevPost.authorName || '') !== String(nextPost.authorFullName || nextPost.authorName || '')) return false;
  if (String(prevPost.authorZodiacSign || '') !== String(nextPost.authorZodiacSign || '')) return false;
  if (String(prevPost.body || '') !== String(nextPost.body || '')) return false;
  if (String(prevPost.parentId || '') !== String(nextPost.parentId || '')) return false;
    const prevAuthorId = String(getAuthorId(prevPost) || '');
  const nextAuthorId = String(getAuthorId(nextPost) || '');
  if (prevAuthorId !== nextAuthorId) return false;

  const prevChildrenLen = Array.isArray(prev.node?.children) ? prev.node.children.length : 0;
  const nextChildrenLen = Array.isArray(next.node?.children) ? next.node.children.length : 0;
  if (prevChildrenLen !== nextChildrenLen) return false;

  return true;
});

export default function ForumThreadView() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fromNotifIdRef = useRef(location?.state?.fromNotifId);
  const navigateRef = useRef(navigate);
  const [thread, setThread] = useState(null);
  
  const [posts, setPosts]   = useState([]);
  const [reply, setReply]   = useState('');
  const POSTS_PAGE_SIZE = 50;
const [postsPage, setPostsPage] = useState(1);
const [postsHasMore, setPostsHasMore] = useState(false);
const [postsLoadingMore, setPostsLoadingMore] = useState(false);

const threadTreeCacheRef = useRef({
  signature: '',
  roots: [],
});

  const loadPostsPage = async (threadId, pageToLoad = 1, { replace = false } = {}) => {
    const p = await getThreadPosts(threadId, { page: pageToLoad, limit: POSTS_PAGE_SIZE });
    const rawItems = Array.isArray(p?.items) ? p.items : (Array.isArray(p?.posts) ? p.posts : []);
    const items = rawItems.map(normalizePostShape);

    setPosts((prev) => {
  if (replace) return items;

  const byId = new Map();
  (Array.isArray(prev) ? prev : []).forEach((it) => {
    const key = String(it?._id || it?.id || '');
    if (key) byId.set(key, it);
  });
  items.forEach((it) => {
    const key = String(it?._id || it?.id || '');
    if (key) byId.set(key, it);
  });
  const next = Array.from(byId.values());
  return next;
});



    setPostsPage(pageToLoad);

    const total = Number(p?.total || 0) || 0;
    const limit = Number(p?.limit || POSTS_PAGE_SIZE) || POSTS_PAGE_SIZE;
    const loadedCount = pageToLoad * limit;
    const hasMore = total > 0 ? loadedCount < total : items.length >= limit;
    setPostsHasMore(hasMore);

    // hidratar contadores de likes desde el backend si vienen en el payload
    try {
      const initCounts = {};
      items.forEach((pp) => {
        const idd = String(pp._id || pp.id || '');
        if (idd) initCounts[idd] = typeof pp.likesCount === 'number' ? pp.likesCount : 0;
      });
      setLikeCountsByReply((prev) => ({ ...prev, ...initCounts }));

      const initLiked = {};
      items.forEach((pp) => {
        const idd = String(pp._id || pp.id || '');
        if (!idd) return;
        if (typeof pp.likedByMe === 'boolean') {
          initLiked[idd] = !!pp.likedByMe;
        }
      });
      setLikesByReply((prev) => ({ ...prev, ...initLiked }));
    } catch {}
  };
  const [focusReply, setFocusReply] = useState(false);
  const [hoverBack, setHoverBack] = useState(false);
  const [hoverBtn, setHoverBtn] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // { id, author, excerpt } o null
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  const [toast, setToast]   = useState('');
  const [sortOrder, setSortOrder] = useState(() => {
    try { return localStorage.getItem('forum_sort') || 'desc'; }
    catch { return 'desc'; }
  }); // 'asc' (antiguos primero) | 'desc' (recientes primero)
  const replyFabRef = useRef(null);
  const likeToastTimerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    try {
      const mq = window.matchMedia('(max-width: 480px)');
      const update = () => setIsMobile(!!mq.matches);
      update();
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } catch { /* no-op */ }
  }, []);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const bottomRef = useRef(null);
  const prevPostsLenRef = useRef(0);
  const emojiPickerRef = useRef(null);
  const MAX_REPLY = 1200;
  // Control de expansión/colapso de subhilos
  const [expandedNodes, setExpandedNodes] = useState({}); // { [postId]: true }

  const highlightPostId = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      const v = sp.get('highlightPostId');
      return v ? String(v) : null;
    } catch {
      return null;
    }
  }, [location.search]);
  useEffect(() => {
    if (!highlightPostId || !posts || posts.length === 0) return;

    try {
      // Expand parent chain if needed
      const postMap = new Map();
      posts.forEach(p => {
        collectAllIdsCached(p).forEach(id => postMap.set(String(id), p));
      });

      let current = postMap.get(String(highlightPostId));
      const toExpand = {};

      while (current) {
        const pid = getParentIdFromAny(current);
        if (!pid) break;
        toExpand[String(pid)] = true;
        current = postMap.get(String(pid));
      }

      if (Object.keys(toExpand).length) {
        setExpandedNodes(prev => ({ ...prev, ...toExpand }));
      }

      // Scroll + glow
      setTimeout(() => {
        const el = document.getElementById(`post-${highlightPostId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('forum-highlight');
          setTimeout(() => el.classList.remove('forum-highlight'), 3200);
        }
      }, 350);
    } catch {}
  }, [highlightPostId, posts]);

  const toggleExpanded = (id) => setExpandedNodes(prev => ({
    ...prev,
    [String(id)]: !prev[String(id)]
  }));

  // Likes por respuesta (persistidos por USUARIO)
  // Detecta el usuario actual desde variables globales o localStorage; fallback a 'anon'
  const [currentUserId, setCurrentUserId] = useState('');

  useEffect(() => {
    const readCurrentUserId = () => {
      const b64decode = (s) => {
        try {
          return decodeURIComponent(atob(s.replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
        } catch { return ''; }
      };

      const readFromToken = () => {
        try {
          const raw = getAuthToken();
          if (!raw) return '';
          const parts = String(raw).split('.');
          if (parts.length < 2) return '';
          const payload = JSON.parse(b64decode(parts[1]) || '{}');
          const id = payload?.sub || payload?.uid || payload?.userId || payload?._id || payload?.id;
          return id ? String(id) : '';
        } catch { return ''; }
      };

      try {
        const byToken = readFromToken();
        if (byToken) return byToken;

        const w = typeof window !== 'undefined' ? window : {};
        const globals = [w.__ZODIACUS_USER, w.__zodiacusUser, w.__user];
        for (const g of globals) {
          const id = g && (g.id || g._id || (g.user && (g.user.id || g.user._id)));
          if (id) return String(id);
        }

        const keys = ['auth:user', 'user', 'me', 'zodiacus:user'];
        for (const k of keys) {
          const raw = localStorage.getItem(k);
          if (!raw) continue;
          try {
            const obj = JSON.parse(raw);
            const id = obj?.id || obj?._id || obj?.user?.id || obj?.user?._id;
            if (id) return String(id);
          } catch {}
        }
        return '';
      } catch {
        return '';
      }
    };

    const apply = () => {
      try { setCurrentUserId(readCurrentUserId()); } catch {}
    };

    apply();

    const onStorage = (e) => {
      const k = String(e?.key || '');
      if (!k || ['accessToken', 'token', 'auth:user', 'user', 'me', 'zodiacus:user'].includes(k)) {
        apply();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  useEffect(() => {
    const timerRef = likeToastTimerRef;
    return () => {
      try {
        const t = timerRef.current;
        if (t) clearTimeout(t);
      } catch {}
    };
  }, []);

  const likesStorageKey = currentUserId ? `forum:replyLikes:${currentUserId}` : 'forum:replyLikes:anon';

  const [likesByReply, setLikesByReply] = useState(() => {
    try {
      const raw = localStorage.getItem(likesStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem(likesStorageKey, JSON.stringify(likesByReply)); } catch {}
  }, [likesByReply, likesStorageKey]);

  // Contador global de likes por respuesta
  const [likeCountsByReply, setLikeCountsByReply] = useState({});
  // Gate anti-rebote para likes: ref persistente + UI map
  const likeBusyRef = useRef({}); // gate persistente entre renders
  const [likeBusyMap, setLikeBusyMap] = useState({}); // solo para deshabilitar botón/estilo

  // --- Add to contacts (frontend-only helper) ---
  // Feature flag para contactos: si el backend no tiene endpoints, ocultamos el botón
  const [contactsAvailable] = useState(() => {
    try { return !sessionStorage.getItem('contacts:disabled'); } catch { return true; }
  });

  async function addToContacts(targetUserId) {
    const uid = String(targetUserId || '');
    if (!uid) return;
    // Redirige al perfil del usuario, sin intentar crear el contacto (evita 404)
    navigate(`/profile/${uid}`);
  }

  const onToggleLike = async (replyId) => {
    const key = String(replyId || '');
    if (!key) return;

    if (likeBusyRef.current[key]) return;

    const token = getAuthToken();
    if (!token) {
      setToast(forumText('Iniciá sesión para dar Me gusta', 'Sign in to like this post'));
      setTimeout(() => setToast(''), 1500);
      return;
    }

    likeBusyRef.current[key] = true;
    setLikeBusyMap(m => ({ ...m, [key]: true }));

    const wasLiked = !!likesByReply[key];
    // Optimismo visual
    setLikesByReply(m => ({ ...m, [key]: !wasLiked }));

    try {
      const res = await toggleReplyLike(key);
      console.debug('[like] toggle result', { key, res });

      if (!res || res.ok === false) {
        // rollback
        setLikesByReply(m => ({ ...m, [key]: wasLiked }));
        const msg = res?.error || 'No se pudo registrar tu Me gusta';
        setToast(msg.includes('auth') ? forumText('Tu sesión venció. Volvé a iniciar sesión.', 'Your session expired. Please sign in again.') : msg);
        setTimeout(() => setToast(''), 1500);
      } else {
        const nextLiked = typeof res.liked === 'boolean' ? res.liked : !wasLiked;

        setLikesByReply(m => ({ ...m, [key]: nextLiked }));
        setLikeCountsByReply(m => ({
          ...m,
          [key]: Math.max(0, (m[key] || 0) + (nextLiked ? 1 : -1))
        }));

        // Refresh autoritativo final
        try {
          const info = await getReplyLikes(key);
          if (info?.ok) {
            if (typeof info.likes === 'number')
              setLikeCountsByReply(m => ({ ...m, [key]: info.likes }));
            if (typeof info.likedByUser === 'boolean')
              setLikesByReply(m => ({ ...m, [key]: info.likedByUser }));
          }
        } catch (err) {
          console.warn('[like] refresh failed', err);
        }
      }
    } catch (err) {
      console.error('[like] error', err);
      setLikesByReply(m => ({ ...m, [key]: wasLiked }));
      setToast(forumText('Error al registrar tu Me gusta', 'Your like could not be recorded'));
      setTimeout(() => setToast(''), 1500);
    } finally {
      likeBusyRef.current[key] = false;
      setLikeBusyMap(m => ({ ...m, [key]: false }));
    }
  };


  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const t = await getThread(slug);
        if (cancelled) return;

        setThread(t);
        setPosts([]);
        setPostsPage(1);
        setPostsHasMore(false);
        await loadPostsPage(t._id, 1, { replace: true });
        if (cancelled) return;
      } catch (e) {
        if (cancelled) return;

        // Si entramos desde una notificación y el hilo ya no existe, limpiamos y volvemos al foro.
        const msg = String(e?.message || '');
        const status = e?.status || e?.response?.status || e?.res?.status;
        const is404 = status === 404 || status === '404' || /\b404\b/i.test(msg) || /not\s*found/i.test(msg);

        const fromNotifId = fromNotifIdRef.current;

        if (is404 && fromNotifId) {
          try {
            await bestEffortMarkForumNotifRead(fromNotifId);
          } catch {}

          try {
            sessionStorage.setItem('forum:toast', forumText('Este hilo ya no existe. La notificación fue limpiada.', 'This thread no longer exists. The notification was cleared.'));
          } catch {}

          navigateRef.current('/foro', {
            replace: true,
            state: { toast: forumText('Este hilo ya no existe. La notificación fue limpiada.', 'This thread no longer exists. The notification was cleared.') }
          });
          return;
        }

        setErr(e?.message || 'No se pudo cargar el hilo');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleLoadMorePosts = async () => {
    if (!thread?._id || postsLoadingMore || !postsHasMore) return;
    setPostsLoadingMore(true);
    try {
      await loadPostsPage(thread._id, postsPage + 1, { replace: false });
    } catch {
      setToast('No se pudieron cargar más respuestas');
      setTimeout(() => setToast(''), 1500);
    } finally {
      setPostsLoadingMore(false);
    }
  };

  useEffect(() => {
    try { localStorage.setItem('forum_sort', sortOrder); } catch {}
  }, [sortOrder]);

  const draftKey = useMemo(() => (thread?._id ? `forum:reply:${thread._id}` : null), [thread]);

  useEffect(() => {
    if (!draftKey) return;
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) setReply(saved);
    } catch {}
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey) return;
    try {
      if (reply) localStorage.setItem(draftKey, reply);
      else localStorage.removeItem(draftKey);
    } catch {}
  }, [reply, draftKey]);

  useEffect(() => {
  try {
    // Evitar scroll automático al entrar por primera vez al thread.
    if (prevPostsLenRef.current === 0) {
      prevPostsLenRef.current = posts.length;
      return;
    }

    // Solo scrollear cuando se agrega una nueva respuesta (incrementa el length).
    if (posts.length > prevPostsLenRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    prevPostsLenRef.current = posts.length;
  } catch {
    prevPostsLenRef.current = posts.length;
  }
}, [posts.length]);

  useEffect(() => {
    if (!showEmojiPicker) return;
    const onDocMouseDown = (e) => {
      try {
        if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
          setShowEmojiPicker(false);
        }
      } catch {}
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('touchstart', onDocMouseDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('touchstart', onDocMouseDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [showEmojiPicker]);

  const postsById = useMemo(() => {
    const m = new Map();
    (posts || []).forEach((p) => {
      collectAllIdsCached(p).forEach((key) => m.set(String(key), p));
    });
    return m;
  }, [posts]);

  const buildThreadTree = useCallback((items, order = 'desc') => {
    const nodesByKey = new Map();
    const nodes = [];

    (items || []).forEach((p) => {
      const node = { post: p, children: [] };
      nodes.push(node);
      collectAllIdsCached(p).forEach((key) => nodesByKey.set(String(key), node));
    });

    const roots = [];
    nodes.forEach((node) => {
      const p = node.post;
      const pid = getParentIdFromAny(p);
      if (pid && nodesByKey.has(String(pid))) {
        nodesByKey.get(String(pid)).children.push(node);
      } else {
        roots.push(node);
      }
    });

    const cmp = (a, b) => {
      const ta = new Date(a.post.createdAt || a.post.updatedAt || 0).getTime();
      const tb = new Date(b.post.createdAt || b.post.updatedAt || 0).getTime();
      return order === 'desc' ? (tb - ta) : (ta - tb);
    };

    const sortDeep = (arr) => {
      arr.sort(cmp);
      arr.forEach((n) => sortDeep(n.children));
    };

    sortDeep(roots);
    return roots;
  }, []);

  const resolveParentInfo = useCallback((p) => {
    try {
      const pid = getParentIdFromAny(p);
      if (!pid) return null;
      const parent = postsById.get(String(pid));
      if (!parent) return null;
      return { author: postAuthorName(parent) };
    } catch { 
      return null; 
    }
  }, [postsById]);


  const submit = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !thread) return;
    setBusy(true); setErr('');
    try {
      const payload = { body: reply.trim() };
      if (replyTo?.id) payload.parentId = replyTo.id;
      const p = await replyThread(thread._id, payload);
      const ensuredParentId = (payload.parentId != null && payload.parentId !== undefined)
        ? payload.parentId
        : (p.parentId != null && p.parentId !== undefined)
          ? p.parentId
          : (p.parent ? (p.parent._id || p.parent.id || p.parent) : undefined);
      const saved = normalizePostShape({
        ...p,
        // ensure the parentId we sent is present even if API omits it in the response
        parentId: ensuredParentId ? String(ensuredParentId) : undefined,
      });
      setPosts(prev => {
  const next = [...prev, saved];
  return next;
});

      // Inicializar contadores y estado de like personal para el nuevo post
      try {
        const newId = String(saved._id || saved.id || '');
        if (newId) {
          setLikeCountsByReply((m) => ({ ...m, [newId]: 0 }));
          setLikesByReply((m) => ({ ...m, [newId]: false }));
        }
      } catch {}
      setReply('');
      setReplyTo(null);
      try { if (draftKey) localStorage.removeItem(draftKey); } catch {}
      setToast('Respuesta publicada');
      setTimeout(() => setToast(''), 1500);
    } catch (e2) {
      setErr(e2.message || 'Error al responder');
    } finally {
      setBusy(false);
    }
  };

  const remaining = Math.max(0, MAX_REPLY - (reply?.length || 0));
  const NEAR_LIMIT = 60; // aviso visual cuando queda poco
  const usedPct = Math.min(100, Math.round(((reply?.length || 0) / MAX_REPLY) * 100));

 const threadTree = useMemo(() => {
  const idsSignature = Array.isArray(posts)
    ? posts.map((p) => String(p?._id || p?.id || '')).join('|')
    : '';
  const signature = `${sortOrder}__${idsSignature}`;

  if (threadTreeCacheRef.current.signature === signature) {
    return threadTreeCacheRef.current.roots;
  }

  const roots = buildThreadTree(posts, sortOrder);
  threadTreeCacheRef.current = { signature, roots };
  return roots;
}, [posts, sortOrder, buildThreadTree]);

  
  const focusReplyBox = useCallback(() => {
    try {
      const form = document.getElementById('replyForm');
      const textarea = document.getElementById('replyBodyView');

      if (form) {
        const rect = form.getBoundingClientRect();
        const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const safeTop = 96;
        const targetY = Math.max(0, currentY + rect.top - safeTop);

        window.scrollTo({
          top: targetY,
          behavior: 'smooth',
        });
      }

      window.setTimeout(() => {
        try {
          if (textarea && typeof textarea.focus === 'function') {
            textarea.focus({ preventScroll: true });
          }
        } catch {
          try { textarea?.focus(); } catch {}
        }
      }, 420);
    } catch {}
  }, []);

  

  return (
    <HelmetProvider>
      <div role="main" style={styles.page} aria-labelledby="threadViewTitle">
        <div role="status" aria-live="polite" style={styles.srOnly}>{toast}</div>
        <Helmet>
          <title>
            {typeof thread?.title === 'string' && thread.title.trim()
              ? `${thread.title} | Foro Zodiacus`
              : 'Foro Zodiacus'}
          </title>
          {typeof thread?.slug === 'string' && thread.slug && (
            <link
              rel="canonical"
              href={`https://zodiacus.app/foro/tema/${encodeURIComponent(thread.slug)}`}
            />
          )}
        </Helmet>

        <style>{`
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(10px) scale(.985); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }

          @keyframes softGlow {
            0%,100% { opacity: .30; filter: blur(18px); transform: scale(1); }
            50% { opacity: .48; filter: blur(25px); transform: scale(1.03); }
          }

          @keyframes zodiacOrbPulse {
            0%,100% { box-shadow: 0 0 28px rgba(218,165,32,.26), inset 0 0 0 1px rgba(255,255,255,.08); }
            50% { box-shadow: 0 0 46px rgba(218,165,32,.45), inset 0 0 0 1px rgba(255,255,255,.12); }
          }

          @keyframes forumPulse {
            0%,100% { box-shadow: 0 0 0 2px rgba(218,165,32,.35), 0 0 18px rgba(218,165,32,.28); }
            50% { box-shadow: 0 0 0 3px rgba(218,165,32,.72), 0 0 34px rgba(218,165,32,.55); }
          }

          .forum-back-btn,
          .forum-action-btn,
          .forum-chip-btn {
            -webkit-tap-highlight-color: transparent;
            touch-action: manipulation;
            user-select: none;
          }

          .forum-back-btn:active,
          .forum-action-btn:active,
          .forum-chip-btn:active {
            transform: translateZ(0) scale(.97);
            filter: brightness(.98);
          }


          .forum-mobile-fixed-nav {
            display: none !important;
          }

          @media (max-width: 768px) {
            .forum-mobile-fixed-nav {
              display: flex !important;
            }
          }

          .forum-highlight {
            box-shadow: 0 0 0 2px rgba(218,165,32,.55), 0 0 26px rgba(218,165,32,.48);
            animation: forumPulse 1.6s ease-in-out infinite;
          }

          .forum-thread-card::before {
            content: '';
            position: absolute;
            inset: 0;
            pointer-events: none;
            background:
              linear-gradient(180deg, rgba(255,255,255,.035), transparent 18%),
              radial-gradient(circle at 50% 0%, rgba(218,165,32,.10), transparent 30%),
              radial-gradient(circle at 96% 20%, rgba(201,147,255,.08), transparent 34%);
            opacity: .9;
          }

          .forum-thread-card::after {
            content: '';
            position: absolute;
            inset: auto -96px -124px auto;
            width: 280px;
            height: 280px;
            pointer-events: none;
            opacity: .035;
            background:
              repeating-conic-gradient(from 0deg, rgba(218,165,32,.28) 0deg 1deg, transparent 1deg 30deg),
              radial-gradient(circle, transparent 0 45%, rgba(218,165,32,.34) 46% 46.5%, transparent 47% 58%, rgba(218,165,32,.22) 59% 59.5%, transparent 60%);
            border-radius: 50%;
            filter: blur(.2px);
            transform: rotate(-14deg);
          }

          .forum-room-shell {
            position: fixed;
            inset: 0;
            pointer-events: none;
            overflow: hidden;
            z-index: 0;
          }

          .forum-room-shell::before {
            content: '';
            position: absolute;
            left: 50%;
            top: 38%;
            width: min(104vw, 620px);
            height: min(104vw, 620px);
            transform: translate(-50%, -50%);
            border-radius: 50%;
            opacity: .03;
            background:
              repeating-conic-gradient(from 0deg, rgba(218,165,32,.22) 0deg 1deg, transparent 1deg 30deg),
              radial-gradient(circle, transparent 0 45%, rgba(218,165,32,.24) 46% 46.5%, transparent 47% 58%, rgba(218,165,32,.16) 59% 59.5%, transparent 60%);
            mask-image: radial-gradient(circle, rgba(0,0,0,.85) 0 56%, transparent 73%);
            -webkit-mask-image: radial-gradient(circle, rgba(0,0,0,.85) 0 56%, transparent 73%);
          }

          .forum-room-shell::after {
            content: '';
            position: absolute;
            inset: -18%;
            background:
              radial-gradient(circle at 18% 20%, rgba(218,165,32,.14), transparent 24%),
              radial-gradient(circle at 82% 14%, rgba(201,147,255,.14), transparent 25%),
              radial-gradient(circle at 50% 76%, rgba(120,42,190,.18), transparent 32%);
            filter: blur(16px);
            opacity: .82;
          }

          @media (max-width: 480px) {
            .forum-thread-card::after {
              right: -130px;
              bottom: -126px;
              top: auto;
              width: 250px;
              height: 250px;
              opacity: .045;
            }

            .forum-thread-card {
              border-top-left-radius: 26px;
              border-top-right-radius: 26px;
            }
          }

          @supports (-webkit-touch-callout: none) {
            #replyBodyView {
              font-size: 16px !important;
            }
          }

        `}</style>

        {/* fondo tipo salas zodiacales: aurora + rueda integrada */}
        <div aria-hidden="true" className="forum-room-shell" />
        <div aria-hidden="true" style={styles.cosmos} />

        {thread && (
          <div className="forum-mobile-fixed-nav" style={styles.mobileFixedNav}>
            <button
              type="button"
              aria-label={forumText('Volver', 'Back')}
              title={forumText('Volver', 'Back')}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={() => navigate(-1)}
              className="forum-back-btn"
              style={styles.replyBackFixed}
            >
              ←
            </button>
            <div style={styles.mobileFixedNavTitle}>
              <strong>Foro</strong>
              <span>{signToEmoji(thread.categoryKey || thread.authorZodiacSign) || '✦'} {thread.categoryKey || thread.authorZodiacSign || 'Zodiacus'}</span>
            </div>
          </div>
        )}

        <div style={styles.card} className="forum-thread-card">
          {thread && (
            <div style={styles.threadTopbar}>
              <button
                type="button"
                aria-label={forumText('Volver', 'Back')}
                onClick={() => navigate(-1)}
                onMouseEnter={() => setHoverBack(true)}
                onMouseLeave={() => setHoverBack(false)}
                className="forum-back-btn"
                style={{
                  ...styles.back,
                  ...(isMobile ? styles.backMobile : null),
                  ...(hoverBack ? styles.backHover : null),
                  ...(isMobile ? styles.hideMobileInlineBack : null)
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 22, lineHeight: '20px' }}>←</span>
                {!isMobile && <span>{forumText('Volver', 'Back')}</span>}
              </button>

              <div style={styles.threadTopCenter}>
                <div style={styles.forumMiniTitle}>Foro</div>
                <div style={styles.forumMiniSign}>
                  {signToEmoji(thread.categoryKey || thread.authorZodiacSign) || '✦'} {thread.categoryKey || thread.authorZodiacSign || 'Zodiacus'}
                </div>
              </div>

              <div aria-hidden="true" style={styles.threadTopActions}>
                <span style={styles.topIcon}>{signToEmoji(thread.categoryKey || thread.authorZodiacSign) || '✦'}</span>
              </div>
            </div>
          )}

          <div style={{ ...styles.header, ...(isMobile ? styles.headerMobile : null) }}>
            <div style={styles.heroCopy}>
              <div style={styles.statusPill}>{thread ? threadStatusLabel(thread) : 'Cargando foro…'}</div>

              <h1
                id="threadViewTitle"
                style={{
                  ...styles.title,
                  ...(isMobile ? styles.titleMobile : null),
                }}
              >
                {thread ? thread.title : 'Cargando…'}
              </h1>
            </div>

            {thread && (
              <div style={styles.signOrbWrap} aria-hidden="true">
                <div style={styles.signOrbGlow} />
                <div style={styles.signOrb}>
                  {signToEmoji(thread.categoryKey || thread.authorZodiacSign) || '✦'}
                </div>
              </div>
            )}
          </div>

          {err && <div style={styles.error}>{err}</div>}

          {(() => {
            const threadAuthorIdResolved = thread ? toIdString(thread.authorId || (thread.author && (thread.author._id || thread.author.id))) : null;
           const threadResolvedPhoto = normalizePhotoUrl(thread?.authorPhotoUrl || thread?.authorPhotoPath || thread?.avatarUrl || thread?.author?.avatarUrl);
            return thread && (
              <div style={{ ...styles.op, borderLeft: `4px solid ${signToColor(thread.authorZodiacSign)}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, rowGap:6, marginBottom:8, flexWrap:'wrap', width:'100%' }}>
                  {threadResolvedPhoto && threadAuthorIdResolved ? (
                    <Link to={`/profile/${threadAuthorIdResolved}`} style={{ display:'inline-flex' }}>
                      <img
                        src={threadResolvedPhoto}
                        alt=""
                        style={{ ...styles.avatar, ...(isMobile ? styles.avatarMobile : null) }}
                        referrerPolicy="no-referrer"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </Link>
                  ) : threadResolvedPhoto ? (
                    <img
                      src={threadResolvedPhoto}
                      alt=""
                      style={{ ...styles.avatar, ...(isMobile ? styles.avatarMobile : null) }}
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : threadAuthorIdResolved ? (
                    <Link to={`/profile/${threadAuthorIdResolved}`} style={{ display:'inline-flex', textDecoration:'none' }}>
                      <div style={{ ...styles.avatarFallback, ...(isMobile ? styles.avatarFallbackMobile : null) }}>
                        {getInitials(thread.authorFullName || thread.authorName || 'Autor')}
                      </div>
                    </Link>
                  ) : (
                    <div style={{ ...styles.avatarFallback, ...(isMobile ? styles.avatarFallbackMobile : null) }}>
                      {getInitials(thread.authorFullName || thread.authorName || 'Autor')}
                    </div>
                  )}
                  {isSeedId(thread.authorId) ? (
                    <span style={styles.authorName}>Zodiacus Oficial 🪐</span>
                  ) : threadAuthorIdResolved ? (
                    <Link to={`/profile/${threadAuthorIdResolved}`} style={styles.authorLink}>
                      {thread.authorFullName || thread.authorName || 'Autor'}
                    </Link>
                  ) : (
                    <span style={styles.authorName}>{thread.authorFullName || thread.authorName || 'Autor'}</span>
                  )}
                  {signToEmoji(thread.authorZodiacSign) && (
                    <span style={styles.zodiac} title={thread.authorZodiacSign}>{signToEmoji(thread.authorZodiacSign)}</span>
                  )}
                  {!isSeedId(thread.authorId) && (
                    <span style={{ ...styles.opBadge, ...(isMobile ? styles.opBadgeMobile : null) }} title="Autor del tema">
                      <span aria-hidden="true" style={{ marginRight: 4, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.25))' }}>👑</span>
                      <span>Creador</span>
                    </span>
                  )}
                  {!isSeedId(thread.authorId) &&
                    threadAuthorIdResolved &&
                    contactsAvailable &&
                    String(threadAuthorIdResolved) !== String(currentUserId) && (
                      <button
                        type="button"
                        onClick={() => addToContacts(threadAuthorIdResolved)}
                        title={forumText('Ver perfil', 'View profile')}
                        style={{
                          ...styles.contactBtn,
                          ...(isMobile ? styles.contactBtnMobile : null),
                        }}
                      >
                        {forumText('Ver perfil', 'View profile')}
                      </button>
                  )}
                  {isMobile ? (
  <div style={styles.opMetaMobile}>
    <span style={styles.meta}>
      {threadStatusLabel(thread)} · {(thread.counters?.views || 0)} vistas
    </span>
  </div>
) : (
  <span style={styles.meta}>
    {threadStatusLabel(thread)} · {(thread.counters?.views || 0)} vistas
  </span>
)}
                </div>
                <div style={styles.opBodyEnhanced}>{cleanForumBody(thread.body)}</div>
              </div>
            );
          })()}

          <div style={styles.sectionTitleRow}>
            <h2 style={styles.sectionTitle}>Respuestas</h2>
            <span style={styles.countChip}>{posts.length}</span>
            <div style={styles.grow} />
            <div style={styles.sortTabs} role="tablist" aria-label="Ordenar respuestas">
              <button
                role="tab"
                aria-selected={sortOrder === 'asc'}
                onClick={() => setSortOrder('asc')}
                style={{ ...styles.sortBtn, ...(sortOrder === 'asc' ? styles.sortBtnActive : null) }}
                title="Antiguas primero"
              >
                Antiguas
              </button>
              <button
                role="tab"
                aria-selected={sortOrder === 'desc'}
                onClick={() => setSortOrder('desc')}
                style={{ ...styles.sortBtn, ...(sortOrder === 'desc' ? styles.sortBtnActive : null) }}
                title="Recientes primero"
              >
                Recientes
              </button>
            </div>
          </div>
          <div style={styles.hr} />
          {(threadTree.length === 0) ? (
            <div style={{ color:'#D7C7EA' }}>{forumText('Aún no hay respuestas.', 'There are no replies yet.')}</div>
          ) : (
            <>
              <ul style={styles.list}>
  {threadTree.map((node, idx) => (
    <ForumPostNode
      key={node.post?._id || node.post?.id || idx}
      node={node}
      idx={idx}
      depth={0}
      suppressChildren={false}
      showToggles={true}
      isMobile={isMobile}
      likesByReply={likesByReply}
      likeCountsByReply={likeCountsByReply}
      likeBusyMap={likeBusyMap}
      currentUserId={currentUserId}
      contactsAvailable={contactsAvailable}
      thread={thread}
      expandedNodes={expandedNodes}
      toggleExpanded={toggleExpanded}
      resolveParentInfo={resolveParentInfo}
      onToggleLike={onToggleLike}
      setToast={setToast}
      likeToastTimerRef={likeToastTimerRef}
      setReplyTo={setReplyTo}
      focusReplyBox={focusReplyBox}
      addToContacts={addToContacts}
      
    />
  ))}
</ul>

              {postsHasMore && (
                <div style={{ display:'flex', justifyContent:'center', marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={handleLoadMorePosts}
                    disabled={postsLoadingMore}
                    style={{
                      ...styles.toggleRepliesBtn,
                      ...(postsLoadingMore ? { opacity: 0.7, cursor: 'wait' } : null)
                    }}
                  >
                    {postsLoadingMore ? 'Cargando…' : 'Ver más respuestas'}
                  </button>
                </div>
              )}
            </>
          )}
          <div ref={bottomRef} style={{ height: 1, scrollMarginBottom: isMobile ? 96 : 48 }} />

          <form
            onSubmit={submit}
            style={{ ...styles.form, ...(isMobile ? styles.formMobile : null) }}
            id="replyForm"
            name="replyForm"
          >
            {replyTo && (
              <div style={styles.replyTarget}>
                <div style={styles.replyTargetHeader}>
                  <strong>Respondiendo a: {replyTo.author}</strong>
                  <button
                    type="button"
                    onClick={() => setReplyTo(null)}
                    aria-label={forumText('Cancelar respuesta', 'Cancel reply')}
                    title={forumText('Cancelar respuesta', 'Cancel reply')}
                    style={styles.replyTargetClose}
                  >
                    ×
                  </button>
                </div>
                {replyTo.excerpt ? (
                  <div style={styles.replyQuote}>“{replyTo.excerpt}”</div>
                ) : null}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(prev => !prev)}
                aria-label={showEmojiPicker ? forumText('Cerrar emojis', 'Close emoji picker') : forumText('Abrir emojis', 'Open emoji picker')}
                title={showEmojiPicker ? forumText('Cerrar emojis', 'Close emoji picker') : forumText('Abrir emojis', 'Open emoji picker')}
                style={{
                  border: '1px solid rgba(218,165,32,0.55)',
                  borderRadius: 999,
                  padding: '8px 11px',
                  background: 'rgba(218,165,32,0.10)',
                  cursor: 'pointer',
                  fontWeight: 900,
                  fontSize: 16,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
                  color: '#FFD978'
                }}
              >
                😀
              </button>
              {showEmojiPicker && (
                <div
                  ref={emojiPickerRef}
                  style={{
                    position: 'absolute',
                    zIndex: 30,
                    right: 0,
                    bottom: 48,
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: 'rgba(24,0,38,0.96)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    boxShadow: '0 18px 44px rgba(0,0,0,0.38), 0 0 0 1px rgba(218,165,32,0.22)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      const ch = emojiData?.emoji || '';
                      setReply(prev => (prev || '') + ch);
                      setShowEmojiPicker(false); // cerrar al elegir
                    }}
                    theme="dark"
                    searchDisabled={true}
                    previewConfig={{ showPreview: false }}
                    lazyLoadEmojis={true}
                    skinTonesDisabled={false}
                    width={300}
                    height={300}
                  />
                </div>
              )}
            </div>
            <label htmlFor="replyBodyView" style={styles.srOnly}>{forumText('Tu respuesta', 'Your reply')}</label>
            <textarea
              id="replyBodyView"
              name="reply"
              rows={4}
              placeholder={forumText('Escribí tu respuesta…', 'Write your reply…')}
              value={reply}
              onChange={(e)=>setReply(e.target.value)}
              onFocus={() => setFocusReply(true)}
              onBlur={() => setFocusReply(false)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(e);
              }}
              maxLength={MAX_REPLY}
              aria-describedby="replyCount"
              style={{
                ...styles.textarea,
                ...(focusReply ? styles.textareaFocus : null),
                ...(isMobile ? styles.textareaMobile : null)
              }}
              required
            />
            <div style={styles.replyMeta}>
              <span
                id="replyCount"
                title={`Restantes: ${remaining}`}
                style={{
                  ...styles.replyCounter,
                  ...(remaining <= NEAR_LIMIT ? { background: 'rgba(239,68,68,0.18)', border: '1px solid #EF4444', color: '#7F1D1D' } : null)
                }}
              >
                {remaining}
              </span>
              <div aria-hidden="true" style={styles.replyBarTrack}>
                <div style={{ ...styles.replyBar, width: `${usedPct}%`, background: usedPct < 85 ? 'linear-gradient(90deg, #DAA520, #FFD54F)' : 'linear-gradient(90deg, #EF4444, #F59E0B)' }} />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy || !reply.trim()}
              onMouseEnter={() => setHoverBtn(true)}
              onMouseLeave={() => setHoverBtn(false)}
              style={{
                ...styles.btn,
                ...(busy || !reply.trim() ? styles.btnDisabled : null),
                ...(hoverBtn && !(busy || !reply.trim()) ? styles.btnHover : null),
                ...(isMobile ? styles.btnMobile : null)
              }}
            >
              {busy ? 'Enviando…' : 'Responder'}
            </button>
          </form>
        </div>
        {thread && <button
          ref={replyFabRef}
          type="button"
          aria-label="Responder"
          title="Responder"
          onClick={focusReplyBox}
          style={styles.replyFab}
        >
          ✍️
        </button>}
      </div>
    </HelmetProvider>
  );
}

const styles = {
  page:{
    minHeight:'100dvh',
    padding:'calc(env(safe-area-inset-top, 0px) + 82px) 8px max(18px, env(safe-area-inset-bottom, 0px))',
    display:'flex',
    alignItems:'flex-start',
    justifyContent:'center',
    background: `
      radial-gradient(900px 420px at 50% -12%, rgba(218,165,32,0.14), transparent 62%),
      radial-gradient(720px 460px at 8% 18%, rgba(120,42,190,0.20), transparent 58%),
      radial-gradient(860px 520px at 108% 74%, rgba(218,165,32,0.08), transparent 58%),
      linear-gradient(180deg, #0b0012 0%, #1c002c 46%, #050008 100%)
    `,
    color:'#F8F1FF',
    boxSizing:'border-box'
  },

  cosmos:{
    position:'fixed',
    inset:0,
    zIndex:0,
    pointerEvents:'none',
    backgroundImage: `
      radial-gradient(circle at 12% 22%, rgba(255,255,255,.18) 0 1px, transparent 1.5px),
      radial-gradient(circle at 78% 16%, rgba(255,231,150,.28) 0 1px, transparent 1.5px),
      radial-gradient(circle at 42% 74%, rgba(255,255,255,.14) 0 1px, transparent 1.5px),
      radial-gradient(460px 300px at 50% 0%, rgba(255,255,255,.06), transparent 70%)
    `,
    backgroundSize:'120px 120px, 180px 180px, 150px 150px, auto',
    mixBlendMode:'screen',
    opacity:.85,
    animation:'softGlow 8s ease-in-out infinite'
  },

  srOnly:{
    position:'absolute',
    width:1,
    height:1,
    padding:0,
    margin:-1,
    overflow:'hidden',
    clip:'rect(0, 0, 0, 0)',
    whiteSpace:'nowrap',
    border:0
  },

  card:{
    width:'100%',
    maxWidth:880,
    position:'relative',
    background:'linear-gradient(180deg, rgba(28,4,46,0.74), rgba(11,0,20,0.88))',
    borderRadius:30,
    padding:'12px 12px 18px',
    boxShadow:'0 24px 70px rgba(0,0,0,0.32), 0 0 0 1px rgba(255,255,255,0.04) inset',
    border:'1px solid rgba(218,165,32,0.12)',
    backdropFilter:'blur(20px)',
    WebkitBackdropFilter:'blur(20px)',
    overflow:'hidden',
    zIndex:1,
    boxSizing:'border-box'
  },

  threadTopbar:{
    display:'grid',
    gridTemplateColumns:'auto 1fr auto',
    alignItems:'center',
    gap:8,
    marginBottom:10,
    position:'relative',
    zIndex:6,
    padding:'0',
    minHeight:42
  },
  threadTopCenter:{ textAlign:'center', minWidth:0 },
  forumMiniTitle:{ color:'#FFD978', fontSize:17, fontWeight:950, letterSpacing:.2, lineHeight:1 },
  forumMiniSign:{ color:'#C993FF', fontSize:12, fontWeight:850, marginTop:4, textTransform:'capitalize' },
  threadTopActions:{ display:'inline-flex', alignItems:'center', justifyContent:'flex-end' },
  topIcon:{
    width:38,
    height:38,
    borderRadius:999,
    display:'inline-flex',
    alignItems:'center',
    justifyContent:'center',
    color:'#FFD978',
    border:'1px solid rgba(218,165,32,0.30)',
    background:'rgba(218,165,32,0.07)',
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.06), 0 0 14px rgba(218,165,32,0.08)',
    fontWeight:950,
    fontSize:20
  },

  header:{
    position:'relative',
    display:'grid',
    gridTemplateColumns:'1fr auto',
    alignItems:'start',
    gap:8,
    padding:'0 6px 12px',
    marginBottom:0,
    zIndex:3
  },
  headerMobile:{
    paddingTop:0,
    paddingBottom:12,
    alignItems:'start',
    gap:6
  },
  headerSticky:{},

  heroCopy:{ flex:'1 1 0', minWidth:0, position:'relative', zIndex:2 },
  statusPill:{
    display:'inline-flex',
    alignItems:'center',
    gap:6,
    color:'#E8C6FF',
    background:'rgba(120,42,190,0.13)',
    border:'1px solid rgba(201,147,255,0.26)',
    borderRadius:999,
    padding:'5px 9px',
    fontWeight:850,
    fontSize:12,
    marginBottom:8
  },

  signOrbWrap:{
    width:70,
    height:70,
    flex:'0 0 70px',
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    position:'relative',
    marginTop:34
  },
  signOrbGlow:{
    position:'absolute',
    inset:8,
    borderRadius:999,
    background:'radial-gradient(circle, rgba(218,165,32,0.28), transparent 68%)',
    filter:'blur(10px)'
  },
  signOrb:{
    position:'relative',
    width:58,
    height:58,
    borderRadius:999,
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    color:'#FFD978',
    fontSize:36,
    background:'radial-gradient(circle at 50% 35%, rgba(255,217,120,0.14), rgba(35,0,55,0.88) 62%)',
    border:'1px solid rgba(218,165,32,0.48)',
    boxShadow:'0 0 20px rgba(218,165,32,0.14), inset 0 0 0 1px rgba(255,255,255,0.06)',
    animation:'zodiacOrbPulse 4.2s ease-in-out infinite'
  },

  back:{
    border:'1px solid rgba(255,255,255,0.13)',
    flex:'0 0 auto',
    background:'rgba(255,255,255,0.035)',
    color:'#F8F1FF',
    borderRadius:999,
    padding:'9px 11px',
    cursor:'pointer',
    fontWeight:900,
    letterSpacing:'.2px',
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.06)',
    transition:'transform .15s, box-shadow .15s, filter .15s',
    display:'inline-flex',
    alignItems:'center',
    justifyContent:'center',
    gap:7,
    minHeight:42,
    minWidth:42,
    lineHeight:1,
    boxSizing:'border-box'
  },
  backHover:{
    transform:'translateY(-1px) scale(1.02)',
    boxShadow:'0 10px 24px rgba(218,165,32,0.20), inset 0 1px 0 rgba(255,255,255,0.10)'
  },
  hideMobileInlineBack:{
    opacity:0,
    pointerEvents:'none',
    visibility:'hidden'
  },
  backMobileInlineVisible:{
    opacity:1,
    pointerEvents:'auto',
    visibility:'visible'
  },

  backMobile:{
    position:'relative',
    top:'auto',
    left:'auto',
    zIndex:8,
    minHeight:42,
    minWidth:42,
    padding:'9px 11px',
    borderRadius:999,
    border:'1px solid rgba(255,255,255,0.13)',
    background:'rgba(255,255,255,0.045)',
    backdropFilter:'blur(10px)',
    WebkitBackdropFilter:'blur(10px)',
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.07)',
    color:'#F8F1FF',
    display:'inline-flex',
    alignItems:'center',
    justifyContent:'center',
    gap:7,
    fontWeight:900,
    letterSpacing:.2,
    transform:'translateZ(0)',
    lineHeight:1,
    boxSizing:'border-box'
  },
  backMobileFloating:{
    position:'fixed',
    top:'calc(env(safe-area-inset-top, 0px) + 10px)',
    left:12,
    zIndex:40,
    background:'rgba(22,0,36,0.72)',
    border:'1px solid rgba(218,165,32,0.22)',
    boxShadow:'0 12px 30px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08)'
  },
  mobileFixedNav:{
    position:'fixed',
    top:'calc(env(safe-area-inset-top, 0px) + 12px)',
    left:12,
    right:12,
    height:54,
    zIndex:2147483647,
    alignItems:'center',
    gap:10,
    padding:'6px 10px',
    borderRadius:999,
    border:'1px solid rgba(218,165,32,0.22)',
    background:'rgba(18,0,32,0.82)',
    boxShadow:'0 16px 40px rgba(0,0,0,0.46), inset 0 1px 0 rgba(255,255,255,0.10)',
    backdropFilter:'blur(18px)',
    WebkitBackdropFilter:'blur(18px)',
    pointerEvents:'auto',
    transform:'translate3d(0,0,0)',
    WebkitTransform:'translate3d(0,0,0)',
    boxSizing:'border-box'
  },
  mobileFixedNavTitle:{
    minWidth:0,
    display:'flex',
    flexDirection:'column',
    color:'#F8F1FF',
    lineHeight:1.05,
    pointerEvents:'none'
  },
  replyBackFixed:{
    position:'relative',
    flex:'0 0 42px',
    width:42,
    height:42,
    borderRadius:999,
    border:'1px solid rgba(218,165,32,0.38)',
    background:'rgba(255,255,255,0.065)',
    color:'#F8F1FF',
    display:'inline-flex',
    alignItems:'center',
    justifyContent:'center',
    fontSize:24,
    fontWeight:950,
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.10)',
    cursor:'pointer',
    lineHeight:1,
    WebkitTapHighlightColor:'transparent',
    pointerEvents:'auto'
  },

  title:{
    margin:0,
    fontFamily:"ui-rounded, system-ui, -apple-system, 'SF Pro Display', 'Segoe UI', Roboto, Arial, sans-serif",
    fontSize:'clamp(28px, 7vw, 42px)',
    lineHeight:1.07,
    fontWeight:950,
    letterSpacing:-.9,
    textAlign:'left',
    whiteSpace:'normal',
    wordBreak:'normal',
    overflowWrap:'break-word',
    hyphens:'none',
    color:'#FFFFFF',
    textShadow:'0 16px 40px rgba(0,0,0,0.42)',
    flex:1,
    minWidth:0,
    paddingBottom:0,
    marginBottom:0,
    borderBottom:'none'
  },
  titleMobile:{
    flexBasis:'auto',
    order:0,
    marginTop:0,
    paddingLeft:0,
    paddingRight:0,
    textAlign:'left',
    fontSize:'clamp(27px, 9.4vw, 39px)',
    lineHeight:1.02,
    letterSpacing:-1,
    color:'#FFFFFF',
    textShadow:'0 16px 40px rgba(0,0,0,0.42)',
    background:'transparent',
    border:'none',
    borderRadius:0,
    paddingTop:0,
    paddingBottom:0,
    boxShadow:'none',
    overflow:'visible',
    maxWidth:'100%',
    boxSizing:'border-box',
    marginLeft:0,
    marginRight:0
  },
  titleScroller:{ display:'block', width:'100%', maxWidth:'100%' },
  titleTextMobile:{ display:'block', width:'100%', maxWidth:'100%' },

  error:{
    color:'#FFD978',
    marginTop:8,
    fontWeight:800,
    background:'rgba(218,165,32,0.10)',
    border:'1px solid rgba(218,165,32,0.35)',
    borderRadius:14,
    padding:10
  },

  op:{
    background:'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.023))',
    border:'1px solid rgba(218,165,32,0.18)',
    borderRadius:22,
    padding:13,
    marginTop:4,
    boxShadow:'0 12px 26px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.055)',
    backdropFilter:'blur(12px)',
    WebkitBackdropFilter:'blur(12px)',
    position:'relative',
    zIndex:3
  },
  opBodyEnhanced:{
    whiteSpace:'pre-wrap',
    lineHeight:1.5,
    color:'#F5EEFF',
    fontSize:'clamp(15px, 4vw, 18px)',
    background:'transparent',
    border:'none',
    borderRadius:0,
    padding:'6px 0 0',
    boxShadow:'none'
  },
  opMetaMobile:{
    display:'flex',
    alignItems:'center',
    justifyContent:'space-between',
    gap:8,
    width:'100%',
    marginTop:4,
    fontSize:12,
    fontWeight:800,
    color:'#BFA9D8'
  },

  sectionTitleRow:{
    display:'flex',
    alignItems:'center',
    gap:9,
    marginTop:16,
    position:'relative',
    zIndex:2,
    flexWrap:'nowrap'
  },
  sectionTitle:{
    margin:0,
    color:'#FFFFFF',
    fontSize:'clamp(18px, 5vw, 22px)',
    fontWeight:950,
    letterSpacing:.1
  },
  countChip:{
    background:'linear-gradient(135deg, #6E12A8, #3F006D)',
    color:'#fff',
    fontWeight:900,
    fontSize:13,
    padding:'4px 10px',
    borderRadius:999,
    boxShadow:'0 0 18px rgba(201,147,255,0.28)',
    border:'1px solid rgba(255,255,255,0.20)'
  },
  hr:{
    height:1,
    background:'linear-gradient(90deg, rgba(201,147,255,0), rgba(201,147,255,0.45), rgba(218,165,32,0.30), rgba(201,147,255,0))',
    border:'none',
    margin:'10px 0 14px',
    position:'relative',
    zIndex:2
  },

  list:{
    listStyle:'none',
    padding:0,
    margin:'10px 0 0',
    display:'flex',
    flexDirection:'column',
    gap:10,
    position:'relative',
    zIndex:2
  },

  post:{
    background:'linear-gradient(180deg, rgba(255,255,255,0.058), rgba(255,255,255,0.025))',
    borderWidth:1,
    borderStyle:'solid',
    borderColor:'rgba(255,255,255,0.095)',
    borderRadius:22,
    padding:14,
    boxShadow:'0 12px 26px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.058)',
    wordBreak:'break-word',
    transition:'transform .12s ease, box-shadow .12s ease, background .12s ease',
    backdropFilter:'blur(10px)',
    WebkitBackdropFilter:'blur(10px)'
  },
  postAlt:{ background:'linear-gradient(180deg, rgba(255,255,255,0.085), rgba(255,255,255,0.04))' },
  postBody:{
    whiteSpace:'pre-wrap',
    lineHeight:1.55,
    color:'#F7F0FF',
    wordBreak:'break-word',
    fontSize:'clamp(15px, 4vw, 17px)',
    marginTop:8
  },

  form:{
    position:'relative',
    marginTop:18,
    display:'flex',
    flexDirection:'column',
    gap:10,
    background:'linear-gradient(180deg, rgba(255,255,255,0.062), rgba(255,255,255,0.026))',
    border:'1px solid rgba(255,255,255,0.095)',
    borderRadius:24,
    padding:12,
    boxShadow:'0 14px 30px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.058)',
    backdropFilter:'blur(12px)',
    WebkitBackdropFilter:'blur(12px)',
    zIndex:3,
    scrollMarginTop:'calc(env(safe-area-inset-top, 0px) + 86px)',
    scrollMarginBottom:'calc(env(safe-area-inset-bottom, 0px) + 120px)'
  },
  formMobile:{ gap:10, padding:12 },

  textarea:{
    padding:'13px 14px',
    border:'1px solid rgba(255,255,255,0.12)',
    borderRadius:14,
    resize:'vertical',
    background:'rgba(0,0,0,0.18)',
    color:'#F8F1FF',
    boxShadow:'inset 0 1px 2px rgba(0,0,0,0.20)',
    outline:'none',
    transition:'border .18s, box-shadow .18s',
    fontSize:16,
    minHeight:86,
    scrollMarginTop:'calc(env(safe-area-inset-top, 0px) + 92px)',
    scrollMarginBottom:'calc(env(safe-area-inset-bottom, 0px) + 128px)'
  },
  textareaFocus:{
    border:'1px solid rgba(218,165,32,0.72)',
    boxShadow:'0 0 0 3px rgba(218,165,32,0.18), 0 12px 28px rgba(218,165,32,0.10)'
  },
  textareaMobile:{ fontSize:16, minHeight:110 },

  replyMeta:{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8, marginTop:4 },
  replyCounter:{
    fontSize:12,
    fontWeight:900,
    color:'#FFD978',
    background:'rgba(218,165,32,0.12)',
    border:'1px solid rgba(218,165,32,0.45)',
    borderRadius:999,
    padding:'2px 8px'
  },
  replyBarTrack:{
    flex:1,
    height:6,
    marginLeft:8,
    borderRadius:999,
    background:'rgba(255,255,255,0.08)',
    overflow:'hidden',
    boxShadow:'inset 0 1px 2px rgba(0,0,0,0.24)'
  },
  replyBar:{ height:'100%', borderRadius:999, transition:'width .12s ease' },

  btn:{
    alignSelf:'flex-start',
    border:'1px solid rgba(218,165,32,0.72)',
    background:'linear-gradient(135deg, #FFE28A 0%, #D9A521 100%)',
    color:'#240033',
    fontWeight:950,
    borderRadius:999,
    padding:'11px 16px',
    cursor:'pointer',
    letterSpacing:.2,
    transition:'background .13s, transform .13s, box-shadow .13s',
    boxShadow:'0 12px 26px rgba(218,165,32,0.24)',
    transform:'translateY(0)'
  },
  btnHover:{ transform:'translateY(-1px)', boxShadow:'0 16px 30px rgba(218,165,32,0.34)' },
  btnDisabled:{ filter:'grayscale(.2) brightness(.98)', opacity:.65, cursor:'not-allowed' },
  btnMobile:{ width:'100%', textAlign:'center' },

  avatar:{
    width:44,
    height:44,
    borderRadius:999,
    objectFit:'cover',
    border:'2px solid #DAA520',
    boxShadow:'0 0 0 3px rgba(218,165,32,0.12), 0 8px 18px rgba(0,0,0,0.28)'
  },
  avatarMobile:{ width:42, height:42 },
  avatarFallback:{
    width:44,
    height:44,
    borderRadius:999,
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    fontSize:13,
    fontWeight:950,
    letterSpacing:.4,
    color:'#FFD978',
    background:'radial-gradient(circle at 50% 30%, rgba(218,165,32,0.22), rgba(75,0,130,0.82))',
    border:'2px solid #DAA520',
    boxShadow:'0 0 0 3px rgba(218,165,32,0.12), 0 8px 18px rgba(0,0,0,0.28)'
  },
  avatarFallbackMobile:{ width:42, height:42, fontSize:13 },

  bullet:{ opacity:.45, color:'#BFA9D8' },
  authorName:{ fontWeight:950, color:'#FFFFFF', fontSize:15 },
  authorLink:{ fontWeight:950, color:'#FFFFFF', textDecoration:'none', fontSize:15 },
  zodiac:{ marginLeft:4, fontSize:16, lineHeight:1, filter:'drop-shadow(0 1px 2px rgba(0,0,0,0.25))' },

  meta:{ color:'#BFA9D8', fontSize:12, marginTop:4, fontWeight:800 },
  metaTimeMobile:{ display:'block', width:'100%', textAlign:'right', marginTop:2, fontSize:12 },

  opBadge:{
    display:'inline-flex',
    alignItems:'center',
    gap:4,
    fontSize:11,
    fontWeight:950,
    color:'#FFD978',
    background:'rgba(218,165,32,0.10)',
    border:'1px solid rgba(218,165,32,0.58)',
    borderRadius:999,
    padding:'3px 9px',
    whiteSpace:'nowrap'
  },
  opBadgeMobile:{ fontSize:10, padding:'2px 7px' },

  contactBtn:{
    border:'1px solid rgba(201,147,255,0.42)',
    background:'rgba(120,42,190,0.12)',
    color:'#E6C8FF',
    borderRadius:999,
    padding:'6px 11px',
    fontWeight:900,
    cursor:'pointer',
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.06)',
    marginLeft:6
  },
  contactBtnMobile:{ padding:'4px 8px', fontSize:11 },
  contactBtnAdded:{
    background:'linear-gradient(135deg, #FFE28A, #D9A521)',
    color:'#240033',
    border:'1px solid rgba(218,165,32,0.75)'
  },

  grow:{ flex:1 },

  sortTabs:{
    display:'inline-flex',
    gap:4,
    background:'rgba(255,255,255,0.045)',
    padding:3,
    borderRadius:999,
    border:'1px solid rgba(255,255,255,0.09)',
    boxShadow:'inset 0 1px 2px rgba(0,0,0,0.18)',
    maxWidth:'52vw',
    overflow:'hidden'
  },
  sortBtn:{
    border:'1px solid transparent',
    background:'transparent',
    color:'#CFA8FF',
    fontWeight:900,
    fontSize:11,
    padding:'7px 10px',
    borderRadius:999,
    cursor:'pointer',
    transition:'background .15s ease, box-shadow .15s ease, transform .12s ease',
    whiteSpace:'nowrap'
  },
  sortBtnActive:{
    background:'linear-gradient(135deg, #FFE28A, #D9A521)',
    color:'#240033',
    border:'1px solid rgba(218,165,32,0.78)',
    boxShadow:'0 0 20px rgba(218,165,32,0.22)'
  },

  replyFab:{
    position:'fixed',
    right:16,
    bottom:'calc(env(safe-area-inset-bottom, 0px) + 36px)',
    zIndex:100,
    border:'1px solid rgba(218,165,32,0.76)',
    background:'linear-gradient(135deg, #FFE28A, #D9A521)',
    color:'#240033',
    borderRadius:999,
    width:52,
    height:52,
    boxShadow:'0 16px 30px rgba(0,0,0,0.32), 0 0 20px rgba(218,165,32,0.22)',
    cursor:'pointer',
    fontSize:21,
    lineHeight:'52px',
    textAlign:'center'
  },

  inReplyTo:{
    marginLeft:6,
    fontSize:11,
    fontWeight:900,
    color:'#E6C8FF',
    background:'rgba(120,42,190,0.14)',
    border:'1px solid rgba(201,147,255,0.32)',
    borderRadius:999,
    padding:'3px 8px',
    whiteSpace:'normal'
  },
  inReplyToMobile:{ display:'block', width:'100%', marginTop:4 },

  replyInlineBtn:{
    border:'1px solid rgba(218,165,32,0.44)',
    background:'rgba(218,165,32,0.10)',
    color:'#FFD978',
    borderRadius:999,
    padding:'6px 10px',
    cursor:'pointer',
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.05)',
    fontWeight:900,
    fontSize:12,
    whiteSpace:'nowrap',
    transition:'transform .12s ease, box-shadow .12s ease'
  },

  toggleRepliesBtn:{
    marginLeft:14,
    marginTop:8,
    border:'1px solid rgba(201,147,255,0.38)',
    background:'rgba(120,42,190,0.14)',
    color:'#E6C8FF',
    borderRadius:999,
    padding:'6px 10px',
    cursor:'pointer',
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.05)',
    fontWeight:900,
    fontSize:12,
    whiteSpace:'nowrap',
    transition:'transform .12s ease, box-shadow .12s ease'
  },
  toggleRepliesBtnAlt:{ background:'rgba(218,165,32,0.10)', color:'#FFD978', border:'1px solid rgba(218,165,32,0.42)' },

  replyTarget:{
    background:'rgba(218,165,32,0.10)',
    border:'1px solid rgba(218,165,32,0.40)',
    borderRadius:14,
    padding:10,
    marginBottom:8,
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.06)',
    scrollMarginTop:'calc(env(safe-area-inset-top, 0px) + 92px)',
    scrollMarginBottom:'calc(env(safe-area-inset-bottom, 0px) + 120px)'
  },
  replyTargetHeader:{
    display:'flex',
    alignItems:'center',
    justifyContent:'space-between',
    gap:8,
    fontWeight:950,
    color:'#FFD978'
  },
  replyTargetClose:{
    border:'1px solid rgba(255,255,255,0.16)',
    background:'rgba(255,255,255,0.06)',
    color:'#FFD978',
    borderRadius:10,
    width:30,
    height:30,
    lineHeight:'28px',
    textAlign:'center',
    cursor:'pointer',
    fontSize:18,
    boxShadow:'inset 0 1px 0 rgba(255,255,255,0.06)'
  },
  replyQuote:{ marginTop:6, fontSize:13, color:'#D7C7EA' }
};
